// Helpers usados por todos los mappers DTO → domain entity.

/** Converts "ROBERT CARLOS" to "Robert Carlos" */
export function toTitleCase(str: string): string {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => (word.length > 0 ? word[0]!.toUpperCase() + word.slice(1) : ''))
    .join(' ');
}

/** Converts "ROBERT" + "CARLOS" to "Robert Carlos" (trims extra whitespace). */
export function buildFullName(firstName: string, lastName: string, middleName?: string | null): string {
  const parts = [toTitleCase(firstName)];
  if (middleName) parts.push(toTitleCase(middleName));
  parts.push(toTitleCase(lastName));
  return parts.join(' ');
}

/** Returns up to 2 initials from a full name: "Robert Carlos" → "RC" */
export function toInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last  = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/** Converts integer flag (0|1) to boolean. */
export function toBool(flag: 0 | 1 | boolean): boolean {
  return flag === 1 || flag === true;
}

/** Converts gender integer to semantic string. */
export function toGender(g: 1 | 2): 'male' | 'female' {
  return g === 1 ? 'male' : 'female';
}

/** Builds full photo URL from a relative filename returned by the API. */
export function buildPhotoUrl(filename: string | null | undefined): string | null {
  if (!filename) return null;
  // If it's already a Data URI (offline-enrolled photo), return as-is
  if (filename.startsWith('data:')) return filename;
  // Otherwise build the backend URL
  const base = import.meta.env.VITE_API_BASE_URL ?? 'https://api.admin.myintelli.net';
  return `${base}/file/${filename}`;
}

/**
 * Removes repeated trailing characters from an access role name.
 * The API often returns names like "QA1 VENNNNN" — this returns "QA1 VEN".
 */
export function cleanRepeatedTrailingChars(name: string): string {
  return name.replace(/(.)\1{3,}$/g, '$1');
}

/** Maps API verification_mode string to domain value. */
export function toVerificationMode(mode: string): 'face' | 'pin' | 'card' | 'unknown' {
  switch (mode.toUpperCase()) {
    case 'USER': return 'face';
    case 'PIN':  return 'pin';
    case 'CARD': return 'card';
    default:     return 'unknown';
  }
}

/** Maps access_type_timelog integer to semantic string. */
export function toAttendanceType(t: 1 | 2): 'entrada' | 'salida' {
  return t === 1 ? 'entrada' : 'salida';
}

/**
 * Capitalizes only when the ENTIRE trimmed string is ALL-UPPERCASE.
 * Mixed-case strings (including all-lowercase) pass through unchanged.
 * Handles null/undefined/empty → ''.
 * Multi-word: each word is capitalized independently.
 * Accented/diacritic chars are handled via toLocaleLowerCase().
 *
 * Examples:
 *   'GERENCIA COMERCIAL' → 'Gerencia Comercial'
 *   'Robert'            → 'Robert'   (mixed-case → passthrough)
 *   'gerencia'          → 'gerencia' (all-lower → passthrough, not all-upper)
 *   null                → ''
 */
export function capitalizeIfUpper(str: string | null | undefined): string {
  if (!str) return '';
  const s = str.trim();
  if (!s) return '';
  if (s !== s.toUpperCase()) return s; // mixed-case → passthrough
  return s
    .split(' ')
    .map((w) => (w.length > 0 ? w[0]!.toUpperCase() + w.slice(1).toLocaleLowerCase() : ''))
    .join(' ');
}
